import { useMemo } from 'react';
import { MULTILINE_KPIS } from '../config/kpi';
import {
  buildHistory,
  buildLineColorMap,
  getLatestLineStatuses,
  prepareChartData,
} from '../data/model';
import { formatDisplayValue } from '../lib/formatters';
import type { BscDataBundle, KpiStatus, SnapshotRow } from '../types/bsc';
import { BreakdownTable } from './BreakdownTable';
import { HistoryChart } from './HistoryChart';
import { StatusBadge } from './StatusBadge';

interface KpiCardProps {
  row: SnapshotRow;
  bundle: BscDataBundle;
  showMoreDetails: boolean;
  showBreakdownTable: boolean;
}

const LINE_STATUS_ORDER: KpiStatus[] = [
  'Critico',
  'Fuori target',
  'Sotto target',
  'In target',
  'Eccellente',
  'Manca dato',
  'Da definire',
  'No target',
];

function thresholdDescription(row: SnapshotRow): string {
  const trend = String(row.Trend_Desiderato ?? '').toLocaleLowerCase('it-IT');
  if (['stabile', 'stabilità', 'stabilita'].includes(trend)) {
    return 'Per un trend stabile, le soglie sono scostamenti assoluti dal target.';
  }
  return 'Le soglie sono confrontate secondo la direzione del trend desiderato.';
}

export function KpiCard({
  row,
  bundle,
  showMoreDetails,
  showBreakdownTable,
}: KpiCardProps) {
  const multiline = MULTILINE_KPIS.has(row.Codice);
  const details = useMemo(() => buildHistory(bundle, row.Codice), [bundle, row.Codice]);
  const chartRows = useMemo(() => prepareChartData(details, multiline), [details, multiline]);
  const lineColors = useMemo(() => buildLineColorMap(chartRows), [chartRows]);
  const latestLineStatuses = useMemo(
    () => (multiline ? getLatestLineStatuses(chartRows, row) : []),
    [chartRows, multiline, row],
  );

  return (
    <article className="kpi-card" id={`kpi-${row.Codice}`}>
      <div className="kpi-card-top">
        <div className="kpi-identity">
          <span className="kpi-code">{row.Codice}</span>
          <div>
            <h3>{row.Nome}</h3>
            <p>{row.Descrizione_KPI}</p>
          </div>
        </div>
        <StatusBadge status={row.Status} />
      </div>

      <div className="kpi-facts">
        <div className="fact fact--primary">
          <span>Valore</span>
          <strong>{multiline ? 'non applicabile' : row.Valore_visualizzato}</strong>
          <small>{multiline ? 'KPI multilinea' : `Aggiornato al ${row.Ultimo_aggiornamento}`}</small>
        </div>
        <div className="fact">
          <span>Target</span>
          <strong>{row.Target_visualizzato}</strong>
          <small>{row.Trend_Desiderato || 'Trend non definito'}</small>
        </div>
        <div className="fact">
          <span>Responsabile</span>
          <strong>{row.Responsabile || 'n.d.'}</strong>
          <small>{row.Frequenza_Rilevazione || 'Frequenza n.d.'}</small>
        </div>
      </div>

      {multiline && latestLineStatuses.length > 0 ? (
        <div className="line-status-summary" aria-label="Distribuzione dello stato delle linee">
          <span className="line-status-label">Ultimo stato per linea</span>
          {LINE_STATUS_ORDER.map((status) => {
            const count = latestLineStatuses.filter((line) => line.line_status === status).length;
            return count > 0 ? (
              <span key={status} className="line-status-chip">
                <StatusBadge status={status} compact />
                <strong>{count}</strong>
              </span>
            ) : null;
          })}
        </div>
      ) : null}

      {showMoreDetails ? (
        <div className="kpi-details-grid">
          <div>
            <span>Obiettivo strategico</span>
            <p>{row.Obiettivo_Strategico_Collegato || 'n.d.'}</p>
          </div>
          <div>
            <span>Formula</span>
            <p>{row.Formula_di_Calcolo || 'n.d.'}</p>
          </div>
          <div>
            <span>Fonte dati</span>
            <p>{row.Fonte_Dati || 'n.d.'}</p>
          </div>
          <div>
            <span>Metodo / validatore</span>
            <p>
              {row.Metodo_Raccolta_Dato || 'n.d.'}
              {row.Validatore_Dato ? ` · ${row.Validatore_Dato}` : ''}
            </p>
          </div>
          <div>
            <span>Soglia minima</span>
            <p>{formatDisplayValue(row.Soglia_Minima, row.Unità_di_Misura)}</p>
          </div>
          <div>
            <span>Soglia eccellenza</span>
            <p>{formatDisplayValue(row.Soglia_Eccellenza, row.Unità_di_Misura)}</p>
          </div>
          <div className="detail-wide">
            <span>Interpretazione soglie</span>
            <p>{thresholdDescription(row)}</p>
          </div>
          {row.Azioni_correttive ? (
            <div className="detail-wide detail-callout">
              <span>Azioni correttive</span>
              <p>{row.Azioni_correttive}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      <HistoryChart
        rows={chartRows}
        registry={row}
        lineColors={lineColors}
        multiline={multiline}
      />

      {multiline && showBreakdownTable ? (
        <BreakdownTable rows={chartRows} registry={row} />
      ) : null}
    </article>
  );
}
